import { useEffect, useState } from 'react';
import useAuth from 'hooks/useAuth';
import { Socket, io } from 'socket.io-client';
import { WEBSOCKET_URL } from '../../../constants';

const RECONNECT_DELAY_MS = 2000;

/** Opens the backend's `/hmi` namespace, which proxies the unit's MQTT traffic, for as long as `enabled`. */
export const useHmiSocket = (enabled: boolean): Socket | null => {
  const { getTokenSilently } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let disposed = false;
    let reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const newSocket = io(`${WEBSOCKET_URL.replace(/\/$/, '')}/hmi`, {
      path: '/socket/socket.io',
      // Resolved per attempt so socket.io's own retries never reuse a token that expired while asleep.
      auth: (callback: (data: Record<string, unknown>) => void) => {
        getTokenSilently()
          .then((accessToken: string) => callback({ accessToken }))
          // An empty payload lets the backend reject the handshake and surface it as a connect_error.
          .catch(() => callback({}));
      },
      reconnection: true,
      reconnectionDelay: RECONNECT_DELAY_MS,
      ackTimeout: 10000,
      transports: ['websocket', 'polling'],
    });

    newSocket.on('disconnect', (reason) => {
      // socket.io retries every reason but a server-closed session, and our own disconnect needs none.
      if (disposed || reason !== 'io server disconnect') {
        return;
      }

      reconnectTimeoutId = setTimeout(() => {
        reconnectTimeoutId = null;
        if (!disposed) {
          newSocket.connect();
        }
      }, RECONNECT_DELAY_MS);
    });

    setSocket(newSocket);

    return () => {
      disposed = true;
      if (reconnectTimeoutId) {
        clearTimeout(reconnectTimeoutId);
      }
      newSocket.disconnect();
      setSocket(null);
    };
  }, [enabled, getTokenSilently]);

  return socket;
};
